import { LIVE_ROLE_STATUSES } from '@remote-medium/shared';
import { unwrap } from '@remote-medium/ui';
import type { InferResponseType } from 'hono/client';

import { admin } from '../../../lib/client.ts';

export type Candidate = InferResponseType<(typeof admin.candidates)[':id']['$get'], 200>['data'];
export type CandidateMatch = InferResponseType<
  (typeof admin.candidates)[':id']['matches']['$get'],
  200
>['data'][number];

/**
 * Everything around a candidate that the candidate endpoint doesn't return:
 * their matches with role and company (best score first, closed roles
 * included), open roles for the salary comparison, the consent link lifetime
 * and the email log.
 */
export async function loadCandidateContext(candidateId: string) {
  const [matches, roles, workflow, emailLog] = await Promise.all([
    unwrap(admin.candidates[':id'].matches.$get({ param: { id: candidateId } })),
    unwrap(admin.roles.$get({ query: {} })),
    unwrap(admin['workflow-config'].$get()),
    unwrap(admin['email-log'].$get({ query: { candidateId } })),
  ]);
  // Roles still open to candidates, for the salary comparison.
  const openRoles = roles.filter((r) => LIVE_ROLE_STATUSES.includes(r.status));
  return { matches, openRoles, consentTokenDays: workflow.consentTokenDays, emailLog };
}

export type CandidateContext = Awaited<ReturnType<typeof loadCandidateContext>>;

/** Confirmed and asked for consent; the caller checks it is still unanswered. */
export function consentAsked(match: CandidateMatch) {
  return match.curatorDecision === 'confirmed' && match.consentRequestedAt !== null;
}

/** Asked, but the consent link ran out unanswered; they can no longer say yes. */
export function consentExpired(match: CandidateMatch, now = new Date()) {
  return (
    consentAsked(match) &&
    match.candidateConsent === 'pending' &&
    match.consentExpiresAt !== null &&
    new Date(match.consentExpiresAt) < now
  );
}
